import * as React from "react";
import { LEAD_ACTIONS, LEAD_STATUS, LEAD_STATUS_COLORS } from "@/constants";
import { Box, Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { DownloadIcon, EyeIcon, InfoIcon } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";

import { campaignsKeys, Lead } from "@/hooks/api/use-campaigns";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column, FilterType } from "@/components/customTable/types";

import { AnalysingIndicator } from "./analysing-indicator";
import { UpdateLeadModal } from "./update-lead-modal";
import { downloadFileFromResponse, formatDate } from "@/utils";
import { toast } from "@/components/core/toaster";
import { API_URL } from "@/runtime-env";

export function LeadList(): React.JSX.Element {
	const { campaignId } = useParams();

	const navigate = useNavigate();
	const [isExportingLeads, setIsExportingLeads] = React.useState(false);

	const {
		items: leads,
		isLoading,
		totalItems,
		tableQuery,
		refetchData: fetchLeads,
	} = useCachedTableStateWithUrl<Lead>({
		queryKey: campaignsKeys.leads(campaignId!),
		endpoint: `/campaigns/${campaignId}/leads`,
	});

	const {
		open: isUpdateLeadsModalOpen,
		data: selectedLeadsToUpdate,
		handleClose: handleUpdateLeadsModalClose,
		handleOpen: handleUpdateLeadsModalOpen,
	} = useDialog<any | null>();

	const selectedRowActions = LEAD_ACTIONS.map((action) => ({
		label: action.label,
		onClick: (rows, isAllDataSelected) => {
			handleUpdateLeadsModalOpen({ rows, isAllDataSelected, action: action.value, label: action.label });
		},
	}));

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "id",
					label: "ID",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "id",
				getCellValue: (row: any) => {
					return (
						<Tooltip title={row.id} arrow>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									fontWeight: "600",
									textOverflow: "ellipsis",
									overflow: "hidden",
									whiteSpace: "nowrap",
									maxWidth: "100px",
								}}
							>
								{row.id}
							</Typography>
						</Tooltip>
					);
				},
			},
			{
				header: {
					id: "Contact",
					label: "Contact",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "contact",
				getCellValue: (row: any) => row.contact?.phone,
			},
			{
				header: {
					id: "disposition",
					label: "Disposition",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "disposition",
				getCellValue: (row: any) => row.disposition,
			},
			{
				header: {
					id: "attempts",
					label: "Attempts",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "attempts",
				getCellValue: (row: any) => {
					if (row.attempts) {
						return row.attempts;
					}

					return " ";
				},
			},
			{
				header: {
					id: "nextAttemptAt",
					label: "Will be enqueued at",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "nextAttemptAt",
				getCellValue: (row: any) => (row.nextAttemptAt ? formatDate(row.nextAttemptAt) : ""),
			},
			{
				header: {
					id: "summary",
					label: "Last Summary",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "summary",
				getCellValue: (row: any) => {
					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							{row?.summary && (
								<Tooltip title={row?.summary} arrow slotProps={{ tooltip: { sx: { lineHeight: 1.8, maxWidth: 400 } } }}>
									<IconButton size="small" color="primary">
										<InfoIcon width={20} height={20} weight="duotone" color="var(--mui-palette-primary-main)" />
									</IconButton>
								</Tooltip>
							)}
							{row?.analysing && <AnalysingIndicator />}
						</Box>
					);
				},
			},
			{
				header: {
					id: "status",
					label: "Status",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "status",
				getCellValue: (row: any) => {
					const status = LEAD_STATUS.find((status) => status.value === row.status);
					// Falling back to the raw value rather than an undefined label. A status the
					// backend adds and this list has not caught up with should look wrong, not look
					// like nothing - an empty chip reads as "no status" and hides the mismatch.
					return (
						<Chip
							color={(LEAD_STATUS_COLORS as Record<string, any>)[row.status] ?? "secondary"}
							variant="outlined"
							size="small"
							label={status?.label ?? row.status ?? "Unknown"}
						/>
					);
				},
			},
			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							isPopoverView
							popoverActions={[
								...(row?.contact?.id
									? LEAD_ACTIONS.map((action) => ({
											label: action.label,
											onClick: () =>
												handleUpdateLeadsModalOpen({
													rows: [row.id],
													isAllDataSelected: false,
													action: action.value,
													label: action.label,
												}),
										}))
									: []),
							]}
							actions={[
								...(row?.contact?.id
									? [
											{
												label: "View Contact Details",
												icon: <EyeIcon width={20} height={20} weight="fill" color="var(--mui-palette-primary-main)" />,
												onClick: () => navigate(`/campaigns/${campaignId}/contacts/${row.contact.id}/view`),
											},
										]
									: []),
							]}
						/>
					</Box>
				),
			},
		];
	}, [campaignId, navigate]);

	const handleExportCampaignLeads = async () => {
		try {
			setIsExportingLeads(true);

			const token = localStorage.getItem("token");
			if (!token) {
				toast.error("Authentication token not found");
				return;
			}


			const params = new URLSearchParams(tableQuery);

			params.delete('limit');
			params.delete('offset');

			const response = await fetch(
				`${API_URL}/campaigns/${campaignId}/export-leads?${params.toString()}`,
				{
					method: "GET",
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (!response.ok) {
				throw new Error(`Export failed: ${response.statusText}`);
			}

			await downloadFileFromResponse(response);

			toast.success("Campaign contacts exported successfully");
		} catch (error: any) {
			const errorMessage = error?.message || "Campaign contacts export failed";
			toast.error(errorMessage);
		} finally {
			setIsExportingLeads(false);
		}
	};

	const renderHeaderButtons = () => {
		return (
			<Button
				startIcon={<DownloadIcon />}
				variant="contained"
				color="info"
				disabled={isExportingLeads || !campaignId}
				loading={isExportingLeads || !campaignId}
				onClick={() => {
					handleExportCampaignLeads();
				}}
			>
				Export Contacts
			</Button>
		);
	}
	return (
		<>
			<CustomTable
				columns={columns}
				data={leads}
				refetchData={fetchLeads}
				isLoading={isLoading}
				totalItems={totalItems}
				tableHeaderTitle="Contacts"
				hasSelectedRows
				selectedRowActions={selectedRowActions}
				enableSelectAllMode
				renderHeaderButtons={renderHeaderButtons}
				filtersOptions={[
					{
						name: "status",
						label: "Status",
						value: [],
						type: FilterType.MULTI_SELECT,
						options: LEAD_STATUS,
					},
					{
						name: "disposition",
						label: "Disposition",
						getOptionsUrl: `/campaigns/${campaignId}/dispositions`,
						fetchLabel: "name",
						fetchValue: "name",
						type: FilterType.FETCH_MULTI_SELECT,
						value: [],
					},
          {
						name: "attempts",
						label: "Attempts",
						value: [],
						type: FilterType.ATTEMPTS,
					},
				]}
			/>
			<UpdateLeadModal
				open={isUpdateLeadsModalOpen}
				onClose={handleUpdateLeadsModalClose}
				selectedLeads={selectedLeadsToUpdate || null}
			/>
		</>
	);
}
